import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  InteractiveElementType,
  Slide,
  SlideDeck,
  SlideTemplateType,
  SlideType,
  TextAlignment,
} from '@/client/courses';
import { ContentEmailComponent } from '@/components/courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '@/components/courses/elements/ContentSMSComponent';
import { SlideImageComponent } from '@/components/slides/elements/SlideImageComponent';
import { SlidesNavigation } from '@/components/slides/SlidesNavigation';
import { useToast } from '@/hooks/useToast';
import { InfoBox } from '@/ui/info-box';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { RichTextRenderer } from '@/ui/rich-text-renderer';
import { FlexContainer } from '@/ui/styled-ui';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const getPopupSlideId = (slide: Slide): string | undefined => {
  if (slide.popupSlide) {
    return typeof slide.popupSlide === 'string'
      ? slide.popupSlide
      : slide.popupSlide.id;
  }
  return slide.tempPopupSlideId;
};

const getPopupSlideContent = (
  slide: Slide,
  allSlides: Slide[],
): Slide | undefined => {
  if (!slide.popupSlide) return undefined;

  const popupId = getPopupSlideId(slide);
  if (!popupId) return undefined;

  const linkedSlide = allSlides.find(({ id }) => id === popupId);

  return linkedSlide;
};

const ViewerContainer = styled.div<{
  isMobile: boolean;
  isPurpleBackground?: boolean;
}>`
  background: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'};
  color: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border-radius: var(--default-border-radius);
  position: relative;
  ${({ isMobile }) => isMobile && 'padding:  var(--small-padding);'}

  .side-padding {
    padding: 0 32px;
  }
`;

const MainContent = styled.div<{ isPurpleBackground?: boolean }>`
  max-width: 1000px;
  flex: 1;
  overflow-y: hidden;
  overflow-x: visible;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  justify-content: flex-start;
  padding: var(--default-padding);
  padding-left: 0;
  padding-right: 0;
  width: 100%;
  min-height: 0;
  scroll-padding-bottom: 100px;

  h1 {
    margin-bottom: var(--medium-padding);
    font-size: 30px;
    font-weight: 700;
    color: ${({ isPurpleBackground }) =>
      isPurpleBackground ? 'var(--white-main)' : 'var(--black-main)'};
  }

  .slide-content {
    font-size: var(--default-font-size);
    line-height: 1.6;
    color: var(--black-main);
  }
`;

const ContentContainer = styled.div<{
  isMobile: boolean;
  isChapter?: boolean;
  hasAccessibilityIcon?: boolean;
}>`
  position: relative;
  padding: ${({ isMobile }) => (isMobile ? '8px' : '32px 32px 0 32px')};
  ${({ hasAccessibilityIcon }) =>
    hasAccessibilityIcon &&
    'padding-top: calc(var(--xlarge-padding) + var(--default-padding));'}
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  ${({ isChapter }) =>
    isChapter &&
    `
    height: 100%;
    flex: 1;
  `}
`;

const SlidesNavigationContainer = styled.div<{ isPurpleBackground?: boolean }>`
  padding: var(--default-padding);
  border-top: 1px solid
    ${({ isPurpleBackground }) =>
      isPurpleBackground ? 'var(--purple-dark)' : 'var(--gray-dark)'};
  position: sticky;
  bottom: 0;
  background-color: ${({ isPurpleBackground }) =>
    isPurpleBackground ? 'var(--purple-dark)' : 'var(--beige-main)'};
  z-index: 1000;
  border-radius: 0 0 var(--default-border-radius) var(--default-border-radius);
`;

const Footnote = styled.p`
  font-size: var(--xsmall-font-size);
  margin-top: var(--medium-padding);
  width: 100%;
  white-space: pre-wrap;
`;

const SlideContentContainer = styled(FlexContainer)<{
  isMobile: boolean;
  textAlignment: TextAlignment;
}>`
  min-height: 0;
  align-items: flex-start;
  overflow: visible;
  ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER &&
    `
    text-align: center;
  `}
  ${({ textAlignment }) =>
    textAlignment === TextAlignment.RIGHT &&
    `
    text-align: right;
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
    @media (orientation: portrait) {
      flex-direction: column-reverse;
      gap: 16px;
    }
  `}
`;

const ImageGridContainer = styled.div<{
  textAlignment: TextAlignment;
  isGallery?: boolean;
  isChapter?: boolean;
  isMobile?: boolean;
}>`
  display: grid;
  grid-auto-flow: ${({ isMobile }) => (isMobile ? 'row' : 'column')};
  grid-template-rows: auto;
  grid-auto-columns: ${({ isGallery }) =>
    isGallery ? 'minmax(0, 1fr)' : '1fr'};
  ${({ isMobile }) =>
    isMobile &&
    `
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  `}
  gap: var(--small-padding);
  width: 100%;
  max-width: ${({ isGallery, isMobile }) =>
    isGallery ? '100%' : isMobile ? '100%' : '375px'};
  min-width: 0;
  box-sizing: border-box;
  align-items: start;
  justify-items: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'end'
      : 'start'};
  justify-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  align-self: ${({ isChapter }) => (isChapter ? 'center' : 'flex-start')};
  margin: 0;
  padding: 0;
  overflow: visible;

  > * {
    height: auto !important;
    min-height: 0;
    overflow: visible !important;
    max-height: none !important;
  }
`;

type SlideTextImageTemplateViewerProps = {
  presentationId?: string;
  onSlideChange?: (index: number) => void;
  onClose?: () => void;
  onComplete?: () => void;
  onPreviousModule?: () => void;
  onLastPageReached?: () => void;
  continueOrFinishButton?: React.ReactNode;
  accessibilityIcon?: React.ReactNode;
  courseId?: string;
  attemptLanguage?: LanguagesEnum;
  rtl?: boolean;
  hasNextModule?: boolean;
  hasPreviousModule?: boolean;
};

export const SlideTextImageTemplateViewer: React.FC<
  SlideTextImageTemplateViewerProps
> = ({
  presentationId,
  onSlideChange,
  onPreviousModule,
  onLastPageReached,
  continueOrFinishButton,
  accessibilityIcon,
  courseId,
  attemptLanguage = LanguagesEnum.EN,
  rtl,
  hasNextModule,
  hasPreviousModule,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPresentation = async () => {
      if (!presentationId || !courseId) return;

      setIsLoading(true);
      try {
        const fetchedSlideDeck: SlideDeck = await client.courses.getSlideDeck(
          courseId,
          presentationId,
        );

        const slidesForLanguage =
          fetchedSlideDeck.translations?.find(
            (translation) => translation.language === attemptLanguage,
          )?.slides || [];

        setSlides(
          slidesForLanguage.sort(
            (a, b) => (a.position || 0) - (b.position || 0),
          ),
        );
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPresentation();
  }, [presentationId, courseId, attemptLanguage]);

  // Get current slide and pop-up slides
  const mainSlides = slides.filter((slide) => slide.type === SlideType.NORMAL);
  const currentSlideData = mainSlides[currentSlideIdx] || {};
  const rawTextAlignment = currentSlideData.textAlignment;
  const effectiveTextAlignment =
    rtl && rawTextAlignment !== TextAlignment.CENTER
      ? TextAlignment.RIGHT
      : rawTextAlignment ?? TextAlignment.LEFT;

  const handlePrevSlide = () => {
    if (currentSlideIdx > 0) {
      const newIndex = currentSlideIdx - 1;
      setCurrentSlideIdx(newIndex);
      onSlideChange?.(newIndex);
    } else if (hasPreviousModule) {
      onPreviousModule?.();
    }
  };

  const handleNextSlide = () => {
    if (currentSlideIdx < mainSlides.length - 1) {
      const newIndex = currentSlideIdx + 1;
      setCurrentSlideIdx(newIndex);
      onSlideChange?.(newIndex);
    } else {
      setCurrentSlideIdx(0);
    }
  };

  useEffect(() => {
    if (currentSlideIdx >= mainSlides.length && mainSlides.length > 0) {
      setCurrentSlideIdx(mainSlides.length - 1);
    }
  }, [mainSlides.length, currentSlideIdx]);

  if (isLoading) {
    return (
      <ViewerContainer isMobile={isMobile()} dir={rtl ? 'rtl' : 'ltr'}>
        <MainContent>
          <ProgressSpinner />
        </MainContent>
      </ViewerContainer>
    );
  }

  const isPurpleBackground =
    currentSlideData.templateType === SlideTemplateType.SUMMARY;
  const isChapter = currentSlideData.templateType === SlideTemplateType.CHAPTER;

  return (
    <ViewerContainer
      isMobile={isMobile()}
      isPurpleBackground={isPurpleBackground}
      dir={rtl ? 'rtl' : 'ltr'}
    >
      {accessibilityIcon}
      <MainContent isPurpleBackground={isPurpleBackground}>
        <ContentContainer
          isMobile={isMobile()}
          isChapter={isChapter}
          hasAccessibilityIcon={!!accessibilityIcon}
        >
          <ProgressHeader
            currentIndex={currentSlideIdx}
            totalCount={mainSlides.length}
            attemptLanguage={attemptLanguage}
            type="slide"
          />
          <SlideContentContainer
            gap={22}
            justify="space-between"
            align={isChapter ? 'center' : 'flex-start'}
            height={isChapter ? '100%' : 'auto'}
            flex={isChapter ? '1' : '0'}
            isMobile={isMobile()}
            textAlignment={effectiveTextAlignment}
          >
            <FlexContainer
              direction="column"
              height={isChapter ? '100%' : 'auto'}
              justify={isChapter ? 'center' : 'flex-start'}
              align={
                effectiveTextAlignment === TextAlignment.CENTER
                  ? 'center'
                  : effectiveTextAlignment === TextAlignment.RIGHT
                  ? 'flex-end'
                  : 'flex-start'
              }
            >
              <RichTextRenderer
                isTitle
                content={currentSlideData.headline || t('content.slide.title')}
                textAlignment={effectiveTextAlignment}
                rtl={rtl}
              />

              <RichTextRenderer
                content={currentSlideData.html || ''}
                className={isPurpleBackground ? 'quill-summary' : ''}
                textAlignment={effectiveTextAlignment}
                rtl={rtl}
              />

              {currentSlideData.infobox &&
                [
                  SlideTemplateType.TEXT_IMAGE,
                  SlideTemplateType.SUMMARY,
                ].includes(currentSlideData.templateType) && (
                  <InfoBox
                    text={currentSlideData.infobox}
                    modalContent={getPopupSlideContent(
                      currentSlideData,
                      slides,
                    )}
                    isSummary={isPurpleBackground}
                    rtl={rtl}
                    attemptLanguage={attemptLanguage}
                  />
                )}
            </FlexContainer>

            {currentSlideData.interactiveElement ? (
              currentSlideData.interactiveElement.type ===
              InteractiveElementType.EMAIL ? (
                <ContentEmailComponent
                  key={currentSlideData.id}
                  id={currentSlideData.id}
                  interactiveElement={currentSlideData.interactiveElement}
                  readOnly={true}
                />
              ) : currentSlideData.interactiveElement.type ===
                InteractiveElementType.SMS ? (
                <ContentSMSComponent
                  key={currentSlideData.id}
                  id={currentSlideData.id}
                  interactiveElement={currentSlideData.interactiveElement}
                  readOnly={true}
                  rtl={rtl}
                />
              ) : null
            ) : (
              currentSlideData.images &&
              currentSlideData.images.length > 0 &&
              [
                SlideTemplateType.TEXT_IMAGE,
                SlideTemplateType.CHAPTER,
                SlideTemplateType.SUMMARY,
              ].includes(currentSlideData.templateType) && (
                <ImageGridContainer
                  isChapter={isChapter}
                  textAlignment={effectiveTextAlignment}
                  isMobile={isMobile()}
                >
                  {currentSlideData.images.map((image, index) => (
                    <SlideImageComponent
                      key={index}
                      image={image}
                      currentEditingSlide={currentSlideData}
                      textAlignment={effectiveTextAlignment}
                      isSummary={
                        currentSlideData.templateType ===
                        SlideTemplateType.SUMMARY
                      }
                    />
                  ))}
                </ImageGridContainer>
              )
            )}
          </SlideContentContainer>
        </ContentContainer>
        {currentSlideData.images &&
          currentSlideData.images.length > 0 &&
          SlideTemplateType.GALLERY === currentSlideData.templateType && (
            <ContentContainer isMobile={isMobile()}>
              <ImageGridContainer
                textAlignment={effectiveTextAlignment}
                isGallery={true}
                isMobile={isMobile()}
                style={{ marginTop: '16px' }}
              >
                {currentSlideData.images.map((image, index) => (
                  <SlideImageComponent
                    key={index}
                    image={image}
                    currentEditingSlide={currentSlideData}
                    textAlignment={effectiveTextAlignment}
                    isGallery={true}
                  />
                ))}
              </ImageGridContainer>
            </ContentContainer>
          )}
        {currentSlideData.footnote &&
          [SlideTemplateType.TEXT_IMAGE, SlideTemplateType.SUMMARY].includes(
            currentSlideData.templateType,
          ) && (
            <ContentContainer className="pt-0" isMobile={isMobile()}>
              <Footnote>{currentSlideData.footnote}</Footnote>
            </ContentContainer>
          )}
      </MainContent>

      <SlidesNavigationContainer isPurpleBackground={isPurpleBackground}>
        <ContentContainer className="pt-0 pl-0 pr-0" isMobile={isMobile()}>
          <SlidesNavigation
            className="mb-0"
            currentSlideIdx={currentSlideIdx}
            totalSlides={mainSlides.length}
            isEditingPopUpSlide={false}
            hasNextModule={hasNextModule}
            hasPreviousModule={hasPreviousModule}
            attemptLanguage={attemptLanguage}
            rtl={rtl}
            onPrevSlide={handlePrevSlide}
            onNextSlide={handleNextSlide}
            onLastPageReached={onLastPageReached}
            continueOrFinishButton={continueOrFinishButton}
          />
        </ContentContainer>
      </SlidesNavigationContainer>
    </ViewerContainer>
  );
};
